import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Droplet } from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuth";

export const MOVEMENTS = [
  {
    n: "One", name: "The Tipping", weeks: "Weeks 1–4", accent: "#A9724E",
    question: "What spills when life tips you over?",
    entries: [
      { w: 1, theme: "Control", practice: "Serving" },
      { w: 2, theme: "Approval", practice: "Hidden Service" },
      { w: 3, theme: "Scarcity", practice: "Generosity" },
      { w: 4, theme: "Defensiveness", practice: "Receiving Correction" },
    ],
  },
  {
    n: "Two", name: "The Pouring", weeks: "Weeks 5–8", accent: "#8a5a37",
    question: "What has settled inside your vessel?",
    entries: [
      { w: 5, theme: "Comparison", practice: "Celebration" },
      { w: 6, theme: "Conflict", practice: "Truth" },
      { w: 7, theme: "Manipulation", practice: "Serving Freely" },
      { w: 8, theme: "Empathy", practice: "Listening" },
    ],
  },
  {
    n: "Three", name: "The Emptying", weeks: "Weeks 9–12", accent: "#6f7d6a",
    question: "What must leave before God fills you?",
    entries: [
      { w: 9, theme: "Hurry", practice: "Slowness" },
      { w: 10, theme: "Noise", practice: "Silence" },
      { w: 11, theme: "Performance", practice: "Rest" },
      { w: 12, theme: "Identity", practice: "Abiding" },
    ],
  },
  {
    n: "Four", name: "The Refining", weeks: "Weeks 13–16", accent: "#9c6b52",
    question: "Who are you becoming?",
    entries: [
      { w: 13, theme: "Confession", practice: "Truthfulness" },
      { w: 14, theme: "Forgiveness", practice: "Release" },
      { w: 15, theme: "Belonging", practice: "Community" },
      { w: 16, theme: "Humility", practice: "Learning" },
    ],
  },
  {
    n: "Five", name: "The Filling", weeks: "Weeks 17–20", accent: "#4f6d7a",
    question: "What is God pouring in?",
    entries: [
      { w: 17, theme: "Submission", practice: "Surrender" },
      { w: 18, theme: "Dependence", practice: "Prayer" },
      { w: 19, theme: "Generosity", practice: "Giving" },
      { w: 20, theme: "Hospitality", practice: "Welcome" },
    ],
  },
  {
    n: "Six", name: "The Outpouring", weeks: "Weeks 21–24", accent: "#A9724E",
    question: "Where are you being sent?",
    entries: [
      { w: 21, theme: "Mission", practice: "Going" },
      { w: 22, theme: "Words", practice: "Testimony" },
      { w: 23, theme: "Legacy", practice: "Rule of Life" },
      { w: 24, theme: "Commissioning", practice: "Blessing" },
    ],
  },
];

export default function Journey() {
  const { user } = usePortalAuth();
  const [currentWeek, setCurrentWeek] = useState(null);

  useEffect(() => {
    portalApi.get("/portal/progress").then((r) => setCurrentWeek(r.data.current_week)).catch(() => {});
  }, []);

  return (
    <div data-testid="portal-journey">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">{user?.cohort || "Winter 2027"} · 24 weeks</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-4">My Journey</h1>
      <p className="text-[#5E4B3C] font-body max-w-2xl mb-3 leading-relaxed">Six movements. Twenty-four weeks. Not lessons to study — a life to live. Each week, one practice quietly exposes what's inside, and Scripture becomes the light that helps you read it.</p>
      <p className="font-serif-display italic text-lg text-[#A9724E] mb-8">Transformation doesn't happen by studying life. It happens by living it.</p>

      {currentWeek > 0 && (
        <div data-testid="journey-progress" className="mb-12">
          <div className="flex items-baseline justify-between mb-2">
            <span className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">Your progress</span>
            <span className="text-sm text-[#5E4B3C] font-body">Week {currentWeek} of 24 · {Math.round((currentWeek / 24) * 100)}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-[#E0D8C9] overflow-hidden">
            <div className="h-full rounded-full bg-[#A9724E] transition-all duration-700" style={{ width: `${Math.round((currentWeek / 24) * 100)}%` }} />
          </div>
          {currentWeek >= 24 && <Link to="/portal/certificate" data-testid="journey-certificate-link" className="inline-block mt-4 text-sm text-[#A9724E] hover:underline">View your graduation certificate →</Link>}
        </div>
      )}

      <div className="space-y-6">
        {MOVEMENTS.map((m) => (
          <div key={m.n} data-testid={`movement-${m.n.toLowerCase()}`} className="bg-white/60 border border-[#E0D8C9] rounded-xl overflow-hidden">
            <div className="p-6 md:p-8 border-b border-[#E0D8C9]" style={{ borderLeft: `4px solid ${m.accent}` }}>
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">Movement {m.n}</span>
                <span className="tracking-overline text-[10px] uppercase text-[#A9724E]">{m.weeks}</span>
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl mt-1" style={{ color: m.accent }}>{m.name}</h2>
              <p className="font-serif-display italic text-lg text-[#2A2825] mt-2">“{m.question}”</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#E0D8C9]">
              {m.entries.map((e) => {
                const active = currentWeek === e.w;
                return (
                  <div key={e.w} className={`p-5 ${active ? "bg-[#EAE3D9]" : ""}`}>
                    <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-2">Week {e.w}{active && <span className="text-[#A9724E]"> · you're here</span>}</p>
                    <p className="font-serif-display text-xl text-[#2A2825]">{e.theme}</p>
                    <p className="text-sm text-[#5E4B3C] font-body mt-1">Practice: {e.practice}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
